import React, { useEffect, useRef, useState } from "react"
import axios, { AxiosError } from "axios"
import { toast } from "react-toastify"
import { CONST_API_RECRUITMENT_CANDIDATES_POST_CREATE, CONST_API_UTILS_EXTRACT_CONTACT_INFO_FROM_CV_ENDPOINT } from "../../../../endpoints"
import Step2ExtractionTable from "./components/step2/Step2ExtractionTable"
import Step2VerificationModal from "./components/step2/Step2VerificationModal"
import Step3ManagementView from "./components/step3/Step3ManagementView"
import { ICreateCandidatStep3Model } from "./core/_models"
import { IHttpErrorResponseModel } from "@common-models/*"
import Step1Selection from "./components/step1/Step1Selection"
import Step1Dropzone from "./components/step1/Step1Dropzone"
import clsx from "clsx"
import CandidatCreate from "../create/CandidatCreate"

export interface ExtractedCandidate {
     id: string
     fileName: string
     civility: string
     firstname: string
     lastname: string
     phoneNumber: string
     email: string
}

const STORAGE_KEY_VIEW = "CANDIDAT_CREATE_VIEW"
const STORAGE_KEY_ITEMS = "CANDIDAT_CREATE_CREATED_ITEMS"

const CandidatImport = () => {
     const [loading, setLoading] = useState(false)
     const [isSubmitting, setIsSubmitting] = useState(false)

     // Mode selection (reset on refresh for Step 1/2)
     const [activeMode, setActiveMode] = useState<"import" | "manual" | null>(null)

     // Persistence Logic: Only management survives refresh
     const [view, setView] = useState<"extraction" | "management">(() => {
          const savedView = localStorage.getItem(STORAGE_KEY_VIEW)
          return savedView === "management" ? "management" : "extraction"
     })

     const [candidates, setCandidates] = useState<ExtractedCandidate[]>([])

     const [createdCandidates, setCreatedCandidates] = useState<ICreateCandidatStep3Model[]>(() => {
          const savedItems = localStorage.getItem(STORAGE_KEY_ITEMS)
          return savedItems ? JSON.parse(savedItems) : []
     })

     const [filesMap, setFilesMap] = useState<Record<string, File>>({})
     const [isDragging, setIsDragging] = useState(false)
     const [previewCandidate, setPreviewCandidate] = useState<{ url: string; data: ExtractedCandidate; index: number } | null>(null)
     const [duplicateEmails, setDuplicateEmails] = useState<string[]>([])
     const [submissionErrors, setSubmissionErrors] = useState<string[]>([])

     const fileInputRef = useRef<HTMLInputElement>(null)

     useEffect(() => {
          localStorage.setItem(STORAGE_KEY_ITEMS, JSON.stringify(createdCandidates))
          if (view === "management") {
               localStorage.setItem(STORAGE_KEY_VIEW, "management")
          } else {
               localStorage.removeItem(STORAGE_KEY_VIEW)
          }
     }, [view, createdCandidates])

     const handleProcessFiles = async (files: FileList | null) => {
          if (!files || files.length === 0) return
          if (candidates.length + files.length > 15) {
               toast.error("Maximum 15 fichiers autorisés.")
               return
          }

          setLoading(true)
          const formData = new FormData()
          const newFilesMap: Record<string, File> = { ...filesMap }

          Array.from(files).forEach(file => {
               formData.append("files[]", file)
               newFilesMap[file.name] = file
          })

          try {
               const response = await axios.post(CONST_API_UTILS_EXTRACT_CONTACT_INFO_FROM_CV_ENDPOINT, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
               })
               const extracted = response.data.candidates.map((c: any) => ({
                    ...c,
                    id: Math.random().toString(36).substring(2, 11),
               }))
               setCandidates(prev => [...prev, ...extracted])
               setFilesMap(newFilesMap)
          } catch (e) {
               const error = (e as AxiosError).response?.data as IHttpErrorResponseModel
               toast.error(error?.detail || "Erreur lors de l'extraction", { autoClose: false })
          } finally {
               setLoading(false)
               if (fileInputRef.current) fileInputRef.current.value = ""
          }
     }

     const handleSubmit = () => {
          setIsSubmitting(true)
          const formData = new FormData()
          candidates.forEach((item, index) => {
               formData.append(`data[${index}][civilite]`, item.civility)
               formData.append(`data[${index}][nom]`, item.lastname)
               formData.append(`data[${index}][prenom]`, item.firstname)
               formData.append(`data[${index}][email]`, item.email)
               formData.append(`data[${index}][tel]`, item.phoneNumber)
               const file = filesMap[item.fileName]
               if (file) formData.append(`fichiers[${index}]`, file)
          })

          axios.post(CONST_API_RECRUITMENT_CANDIDATES_POST_CREATE, formData)
               .then(r => {
                    setCreatedCandidates(r.data)
                    setCandidates([])
                    setView("management")
                    toast.success("Candidats créés !")
               })
               .catch((e: AxiosError<any>) => {
                    const errorData = e.response?.data
                    if (errorData?.customCode === "DUPLICATE_CANDIDATE" && errorData.candidates) {
                         setDuplicateEmails(errorData.candidates.map((c: any) => c.email))
                    }
                    setSubmissionErrors([errorData?.details || "Erreur de création."])
               })
               .finally(() => setIsSubmitting(false))
     }

     const resetToChoice = () => {
          setActiveMode(null)
          setCreatedCandidates([])
          setCandidates([])
          setView("extraction")
          localStorage.removeItem(STORAGE_KEY_VIEW)
          localStorage.removeItem(STORAGE_KEY_ITEMS)
     }

     return (
          <div className="d-flex flex-column gap-8">
               {/* Hidden file input for batch import */}
               <input type="file" multiple ref={fileInputRef} className="d-none" onChange={e => handleProcessFiles(e.target.files)} accept=".pdf" />

               {/* 1. SELECTION SCREEN */}
               {view === "extraction" && !activeMode && <Step1Selection onSelectImport={() => setActiveMode("import")} onSelectManual={() => setActiveMode("manual")} />}

               {/* 2. ACTIVE WORKFLOW (Extraction View) */}
               {view === "extraction" && activeMode && (
                    <div className="animate__animated animate__fadeIn d-flex flex-column gap-5">
                         {/* Header Status with Modify Button */}
                         <div
                              className={clsx(
                                   "d-flex align-items-center justify-content-between p-5 rounded-4 border border-opacity-20 shadow-sm",
                                   activeMode === "import" ? "bg-light-primary border-primary" : "bg-light-info border-info"
                              )}
                         >
                              <div className="d-flex align-items-center">
                                   <div className="symbol symbol-35px me-3">
                                        <div className={clsx("symbol-label", activeMode === "import" ? "bg-primary" : "bg-info")}>
                                             <i className={clsx("fas text-white", activeMode === "import" ? "fa-robot" : "fa-user-edit")}></i>
                                        </div>
                                   </div>
                                   <span className={clsx("fw-boldest text-uppercase", activeMode === "import" ? "text-primary" : "text-info")}>
                                        MODE {activeMode === "import" ? "Importation" : "Saisie Manuelle"} sélectionné
                                   </span>
                              </div>
                              <button className={clsx("btn btn-sm fw-boldest px-6", activeMode === "import" ? "btn-primary" : "btn-info")} onClick={resetToChoice}>
                                   MODIFIER
                              </button>
                         </div>

                         {/* DYNAMIC CONTENT SWITCH */}
                         {activeMode === "import" ? (
                              <>
                                   {/* Dropzone - Always visible */}
                                   <Step1Dropzone
                                        isExtracting={loading}
                                        isDragging={isDragging}
                                        setIsDragging={setIsDragging}
                                        onDrop={handleProcessFiles}
                                        fileInputRef={fileInputRef}
                                   />

                                   {/* Verification Table if items exist */}
                                   {candidates.length > 0 && (
                                        <Step2ExtractionTable
                                             candidates={candidates}
                                             isSaving={isSubmitting}
                                             duplicateEmails={duplicateEmails}
                                             submissionErrors={submissionErrors}
                                             onClearErrors={() => setSubmissionErrors([])}
                                             onUpdate={(idx, field, val) => {
                                                  const newArr = [...candidates]
                                                  newArr[idx] = { ...newArr[idx], [field]: val }
                                                  setCandidates(newArr)
                                             }}
                                             onRemove={idx => setCandidates(prev => prev.filter((_, i) => i !== idx))}
                                             onPreview={idx => {
                                                  const file = filesMap[candidates[idx].fileName]
                                                  if (file) setPreviewCandidate({ url: URL.createObjectURL(file), data: candidates[idx], index: idx })
                                             }}
                                             onSave={handleSubmit}
                                        />
                                   )}
                              </>
                         ) : (
                              /* Dynamic Manual Component */
                              <CandidatCreate />
                         )}
                    </div>
               )}

               {/* 3. FINAL MANAGEMENT (Step 3) */}
               {view === "management" && createdCandidates.length > 0 && (
                    <Step3ManagementView items={createdCandidates} onUpdateItems={setCreatedCandidates} onAddAnother={resetToChoice} />
               )}

               <Step2VerificationModal
                    preview={previewCandidate}
                    duplicateEmails={duplicateEmails}
                    onClose={() => setPreviewCandidate(null)}
                    onNavigate={idx => {
                         const file = filesMap[candidates[idx].fileName]
                         if (file) setPreviewCandidate({ url: URL.createObjectURL(file), data: candidates[idx], index: idx })
                    }}
                    navInfo={{
                         prevIdx: previewCandidate && previewCandidate.index > 0 ? previewCandidate.index - 1 : -1,
                         nextIdx: previewCandidate && previewCandidate.index < candidates.length - 1 ? previewCandidate.index + 1 : -1,
                         current: previewCandidate ? previewCandidate.index + 1 : 0,
                         total: candidates.length,
                    }}
                    onUpdate={(field, val) => {
                         if (previewCandidate) {
                              const newArr = [...candidates]
                              newArr[previewCandidate.index] = { ...newArr[previewCandidate.index], [field]: val }
                              setCandidates(newArr)
                              setPreviewCandidate({ ...previewCandidate, data: newArr[previewCandidate.index] })
                         }
                    }}
               />
          </div>
     )
}

export default CandidatImport
